<template>
  <app-modal content-class="col-sm-12 col-md-4 col-lg-4 col-xl-3 p-0" @close="close" :top="true" :overflow="true" :closeable="false">
    <b-card no-body>
      <b-card-body class="p-4" :title="isNew ? 'Create new employee' : `Edit employee #${id}`">
        <div class="panel-close d-md-none" @click="close"><i class="ion ion-md-close"></i></div>
        <EmployeeEditor
          v-if="employeeLoaded && !placeOnMapOnlyMode"
          :id="id"
          :point="point"
          :has-header="false"
          :request-extra-options="requestExtraOptions"
          padding-off
          @cancel="close"
          @save="afterChildSave"
          @destroy="close"
        >
          <template v-if="isNew" #extend>
            <div class="form-check" v-if="canMutateUser">
              <input
                class="form-check-input"
                type="checkbox"
                id="createUser"
                :checked="createRelatedUser"
                @change="createRelatedUser = !createRelatedUser"
              />
              <label class="form-check-label" for="createUser">
                create related user
              </label>
            </div>
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                id="infinityCheck"
                :checked="placeOnMapEnabled"
                @change="placeOnMapEnabled = !placeOnMapEnabled"
              />
              <label class="form-check-label" for="infinityCheck">
                place on map
              </label>
            </div>
            <b-form-group label="Floor" v-if="placeOnMapEnabled">
              <MapFloorSelect class="w-100" @change="selectedFloor = $event" :active-floor="selectedFloor" />
            </b-form-group>
          </template>
          <template v-else #extend="{canSave}">
            <div class="form-check" v-if="canMutateUser">
              <input
                class="form-check-input"
                type="checkbox"
                id="deleteUser"
                :checked="deleteRelatedUser"
                :disabled="!canSave"
                @change="deleteRelatedUser = !deleteRelatedUser"
              />
              <label class="form-check-label" for="deleteUser">
                delete related user
              </label>
            </div>
          </template>
        </EmployeeEditor>

        <template v-if="placeOnMapOnlyMode">
          <b-form-group label="Floor">
            <MapFloorSelect class="w-100" @change="selectedFloor = $event" :active-floor="selectedFloor" />
          </b-form-group>
          <div class="btn-panel my-2">
            <div class="ml-auto"></div>
            <b-btn type="button" variant="primary" @click="savePlaceOnMapOnly">Save</b-btn>
            <b-btn type="button" variant="default" @click="close">Cancel</b-btn>
          </div>
        </template>
      </b-card-body>
    </b-card>
  </app-modal>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import Id, { isLocalId } from '@/utils/Id'
import EmployeeEditor from '@/components/employee/EmployeeEditor'
import { DEFAULT_POINT_LAT_LON, POINT_ICONS, POINT_UPDATE } from '@/store/modules/point'
import MapFloorSelect from '@/components/map/MapFloorSelect'
import dayjs from 'dayjs'
import { SERVER_DATE_TIME_FORMAT } from '@/components/desk/utils'
import { DESK_LOAD } from '@/store/modules/desk'
import * as PN from '@/utils/PermissionNames'

export default {
  name: 'Employee',
  components: {
    MapFloorSelect,
    EmployeeEditor
  },
  data: () => ({
    id: null,
    employeeLoaded: false,
    placeOnMapEnabled: true,
    selectedFloor: null,
    deleteRelatedUser: false,
    createRelatedUser: false
  }),
  computed: {
    ...mapGetters('rbac', ['can']),
    ...mapGetters('floor', ['floors']),
    ...mapGetters('point', ['getPoint']),
    ...mapGetters('employee', ['getEmployee']),
    ...mapGetters('department', ['getDepartment']),
    ...mapGetters('settings', ['scimUsersSyncEnabled']),
    canMutateUser() {
      return this.can(PN.USER_CRUD) && !this.scimUsersSyncEnabled
    },
    requestExtraOptions() {
      return {
        handleUser: this.deleteRelatedUser || this.createRelatedUser
      }
    },
    point() {
      if (this.infinityEvent) {
        const point = this.infinityEvent.desk.point
        return { ...point, desk: this.infinityEvent.desk.id }
      }
      return null
    },
    isNew() {
      return isLocalId(this.id)
    },
    infinityEvent() {
      return this.employee?.events?.find((v) => v.end === 'infinity')
    },
    employee() {
      return this.getEmployee(this.id)
    },
    placeOnMapOnlyMode() {
      return !!this.$route.params.placeOnMap
    }
  },
  methods: {
    ...mapActions('desk', ['deskSave', 'deskEventSave']),
    ...mapActions('point', ['pointDestroy', 'pointSave']),
    ...mapActions('employee', ['employeeLoad']),
    ...mapMutations('point', {
      pointUpdate_: POINT_UPDATE
    }),
    ...mapMutations('desk', {
      deskLoad_: DESK_LOAD
    }),
    init(route) {
      if (route.params && route.params.employeeId) {
        this.id = route.params.employeeId
        if (this.isNew) {
          this.employeeLoaded = true
          this.selectedFloor = this.floors.length ? this.floors[0].id : null
        } else {
          this.employeeLoad({
            id: this.id,
            params: {
              expand: 'events,events.desk,events.desk.point'
            }
          }).then(() => {
            if (this.infinityEvent?.desk) {
              this.deskLoad_(this.infinityEvent.desk)
            }
            this.employeeLoaded = true
          })
        }
      } else {
        throw new Error('Id is not present on employee route')
      }
    },
    close() {
      this.$router.push({ name: 'employees', query: { prevEmployee: this.id } })
    },
    async afterChildSave({ employeeId, pointId }) {
      if (((this.isNew && this.placeOnMapEnabled) || this.placeOnMapOnlyMode) && this.selectedFloor && employeeId) {
        if (pointId) {
          // update floor
          await this.pointSave({
            id: pointId,
            floor_id: this.selectedFloor
          })
        } else {
          const newPointId = await this.pointSave({
            id: Id(),
            floor_id: this.selectedFloor,
            lat_lon: DEFAULT_POINT_LAT_LON,
            icon_id: POINT_ICONS.EMPLOYEE
          })

          const newDeskId = await this.deskSave({
            desk: {
              id: Id(),
              point_id: newPointId
            }
          })

          await this.deskEventSave({
            deskEvent: {
              id: Id(),
              desk_id: newDeskId,
              start: dayjs().format(SERVER_DATE_TIME_FORMAT),
              end: 'infinity',
              employee_id: employeeId
            }
          })
        }

        this.id = employeeId
      }

      this.close()
    },
    savePlaceOnMapOnly() {
      this.afterChildSave({ employeeId: this.employee.id })
    }
  },
  created() {
    this.init(this.$route)
  },
  beforeRouteUpdate(to, from, next) {
    this.init(to)
    next()
  }
}
</script>

<style lang="scss">
.password-copy {
  cursor: pointer;
}

.panel-close {
  position: absolute;
  right: 25px;
  top: 20px;
  font-size: 24px;
  cursor: pointer;
}
</style>
